import _ from 'lodash';
import { SimpleTextCollectionCore } from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/SimpleTextCollectionCore';
import { Button, Tooltip } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../InteractionStep';
import { AutoCritiqueButton, AutoCritiqueAlert, CardAlert } from '../../TempComponents';
import { Card } from '../../Card';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { useCallback, useContext, useMemo, useState, useRef, useEffect, memo } from 'react';
import {
  DEFAULT_TEXTCOLLECTION_SUBTITLE,
  DEFAULT_TEXTCOLLECTION_TITLE,
  InteractionStepType,
  TextCollectionStepResponse,
} from '@scale/shared/types/chat';
import { useTrans } from '../../hooks/useTrans';
import useAutoCritique from '../../hooks/useAutoCritique';
import {
  extractVariablesFromTextCollectionResponse,
  useTextCollectionStepValidation,
} from '../../utils';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { MultiChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/MultiChatTaskContext';
import { TextCollectionResponseCompact } from '../TextCollectionResponseCompact';
import useLocalStorageWithExpiry, {
  getLocalStorageKey,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageWithExpiry';
import { ChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import {
  useChatStoreExtract,
  useStepParams,
  useStepResponse,
  useMostRecentResponseFullObject,
  useMostRecentPrompt,
  useMostRecentResponseForStepType,
  useChatConfig,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { StepSubmissionConfirmationModal } from 'react-scale/components/scalers/tasks/ThreadedInteraction/StepSubmissionConfirmationModal';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { StepWidthWrapper, StepWidth } from '../../step/StepWidthWrapper';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';
import { StepCard } from '../../step/StepCard';
import { PinButton } from '../../step/PinButton';
import { CommentButton } from '../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';

const DEFAULT_EMPTY_RESPONSE = { annotations: {} };

const TextCollectionTaskComponent: InteractionStepTaskComponent =
  // eslint-disable-next-line complexity
  ({ readOnly, responseIndex, size, complete, isPinnedView }) => {
    const taskContext = useContext(ChatTaskContext);
    const templateVariables = useTemplateVariableStore.getState();
    const _step = useStepParams<InteractionStepType.TextCollection>(responseIndex);
    const response = useStepResponse<TextCollectionStepResponse>(responseIndex);
    const config = useChatConfig();
    const lastPrompt = useMostRecentPrompt(responseIndex);
    const lastResponse = useMostRecentResponseFullObject(responseIndex);
    const lastResponseOutput = lastResponse?.output ?? '';
    const lastModelResponseEditorResponse = useMostRecentResponseForStepType(
      InteractionStepType.ModelResponseEditor,
      responseIndex,
    );

    let chatIndex = undefined;
    const multiChatTaskContext = useContext(MultiChatTaskContext);
    if (multiChatTaskContext) {
      chatIndex = multiChatTaskContext.chatIndex;
    }

    const step = useMemo(() => {
      const patchedStep = _.cloneDeep(_step);
      patchedStep.params.fields = patchedStep.params.fields.map(field => ({
        ...field,
        disable_pasting:
          (taskContext.disablePastingTextCollectionFieldIds ?? []).includes(field.field_id) ||
          taskContext.disablePastingAllTextCollectionFields,
      }));
      return patchedStep;
    }, [
      _step,
      taskContext.disablePastingTextCollectionFieldIds,
      taskContext.disablePastingAllTextCollectionFields,
    ]);

    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });

    const { t } = useTrans();
    const lastSavedResponse = response?.context.response ?? DEFAULT_EMPTY_RESPONSE;
    const localSaveKey = getLocalStorageKey({
      assignmentId: taskContext.assignmentId,
      stepType: _step.type,
      responseIndex,
      disabled: taskContext.isPipelineV2,
      chatIndex,
    });
    const [currentResponse, setCurrentResponse, clearBackup] =
      useLocalStorageWithExpiry<TextCollectionResponse>(lastSavedResponse, localSaveKey);

    const markStepAsIncompletedByIndex = useChatStoreExtract(
      state => state.markStepAsIncompletedByIndex,
    );
    const markStepAsCompletedByIndex = useChatStoreExtract(
      state => state.markStepAsCompletedByIndex,
    );
    const canUpdateCompletedState = response && !_.isNil(responseIndex);

    /*
     * We need to allow the submission of an empty response if no fields are required and a response
     * for this step has not been submitted yet. This means we should allow submission even if the
     * currentResponse and lastSavedResponse are equal and both empty.
     */
    const isValidEmptyResponse =
      !response &&
      _.every(step.params.fields, field => !field.required) &&
      _.isEqual(currentResponse.annotations, DEFAULT_EMPTY_RESPONSE.annotations);

    const cardRef = useRef<HTMLDivElement>(null);

    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [submitCodeErrors, setSubmitCodeErrors] = useState<Record<string, string>>({});
    const setSubmitCodeError = useCallback((field_id: string, err: string) => {
      setSubmitCodeErrors(prevErrors => ({
        ...prevErrors,
        [field_id]: err,
      }));
    }, []);
    const submitCodeError = useMemo(() => {
      return Object.values(submitCodeErrors).find(error => error.length > 0);
    }, [submitCodeErrors]);

    /* The output should always be the last response output unless this is a before step.
     * If this is a before step, we use the last prompt output.
     */
    const output =
      !_.isNil(responseIndex) && responseIndex < config.before.length
        ? lastPrompt
        : lastResponseOutput;

    const autoCritiqueHook = useAutoCritique();
    const autoCritiqueConfig = taskContext.autoCritiqueConfig?.textCollection;
    const autoCritiqueLabel = autoCritiqueConfig?.renameButton;

    const fetchAutoCritique = useCallback(() => {
      if (lastPrompt) {
        autoCritiqueHook.getAutoCritique({
          scope: 'textCollection',
          projectId: taskContext.projectId,
          taskId: taskContext.taskId,
          assignmentId: taskContext.assignmentId,
          workerId: taskContext.workerId,
          variables: {
            ...config.templateVariables,
            ...extractVariablesFromTextCollectionResponse(currentResponse),
            prompt: lastPrompt,
            response: lastResponseOutput,
          },
        });
      }
    }, [
      lastPrompt,
      autoCritiqueHook,
      taskContext.projectId,
      taskContext.taskId,
      taskContext.assignmentId,
      taskContext.workerId,
      config.templateVariables,
      currentResponse,
      lastResponseOutput,
    ]);

    const resultChanged = useMemo(
      () => !_.isEqual(currentResponse.annotations, lastSavedResponse.annotations),
      [currentResponse, lastSavedResponse],
    );

    const shouldShowAlert = useMemo(() => {
      return resultChanged && !complete && !readOnly;
    }, [resultChanged, complete, readOnly]);

    /*
     * Mark the step as incomplete if the local response differs from the global response and the step is marked as complete
     * This ensures that if there is a local storage save, the step is correctly marked as incomplete
     */
    useEffect(() => {
      if (complete && resultChanged) {
        markStepAsIncompletedByIndex(responseIndex);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // only run on mount

    const shouldPreventRetroactiveEdits = !!step.params.prevent_retroactive_edits && complete;

    const handleSubmitStep = useCallback(async () => {
      const isValid = await onSubmit(
        {
          type: _step.type,
          context: {
            response: currentResponse,
          },
          output,
        },
        responseIndex,
        _step,
        !!step.params.reset_history_on_change,
      );
      if (isValid) {
        clearBackup();
      }
    }, [
      currentResponse,
      output,
      onSubmit,
      clearBackup,
      responseIndex,
      _step,
      step.params.reset_history_on_change,
    ]);

    const { canSubmit, submitTooltip } = useTextCollectionStepValidation({
      readOnly,
      resultChanged,
      submitCodeError,
      response: currentResponse,
      fields: step.params.fields,
      templateVariables,
      isValidEmptyResponse,
      allAttachmentsViewed: true,
      autoCritiqueIsLoading: autoCritiqueHook.critiqueData.loading,
    });

    if (size === 'compact') {
      return (
        <StepWidthWrapper>
          <TextCollectionResponseCompact
            params={{ ...step.params, attachments: [] }}
            response={currentResponse}
          />
        </StepWidthWrapper>
      );
    }

    return (
      <CardAlert
        message={response && shouldShowAlert ? 'Please save the section below.' : undefined}
      >
        <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
          <StepCard readOnly={readOnly}>
            <StepCard.Header>
              <StepCard.HeaderLeft>
                <StepCard.Icon icon="question-circle" />
                <StepCard.HeaderColumn>
                  <StepCard.Title>{step.title || DEFAULT_TEXTCOLLECTION_TITLE}</StepCard.Title>
                  <StepCard.Subtitle>
                    {step.subtitle || DEFAULT_TEXTCOLLECTION_SUBTITLE}
                  </StepCard.Subtitle>
                </StepCard.HeaderColumn>
              </StepCard.HeaderLeft>
              <StepCard.HeaderRight>
                {!taskContext.enableUnifiedFeedback &&
                  step.type === InteractionStepType.TextCollection && (
                    <CommentButton
                      visible={true}
                      childId={constructStepChildId(step)}
                      turnIndex={step.turn}
                    />
                  )}
                <PinButton responseIndex={responseIndex} />
              </StepCard.HeaderRight>
            </StepCard.Header>
            <StepCard.Body>
              <div ref={cardRef}>
                {step.params?.show_last_response && (
                  <Card>
                    <ChatMarkdown
                      cardRef={cardRef}
                      className="text-sm"
                      markdown={lastResponseOutput}
                      enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                      enableInlineComments={step.params.enable_inline_comments}
                      childId={constructStepChildId(step)}
                      overrideChildId={
                        lastResponse ? `${lastResponse.type}-${lastResponse.index}` : undefined
                      }
                      turnIndex={step.turn}
                      enableSingleDollarTextMath={
                        taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                      }
                      enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                      enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                      enableExecutableCodeBlocks={
                        taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                        taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                      }
                    />
                  </Card>
                )}
                <div className="flex justify-center w-full p-4 my-2 rounded-md">
                  <div className="relative h-fit min-h-[180px] max-[800px]:w-72 min-[800px]:w-[90%]">
                    <ChatStepChildIdContext.Provider
                      value={{
                        childId: taskContext.responseTemplatingMode
                          ? constructStepChildId(step)
                          : undefined,
                        metadata: {
                          stepIndex: responseIndex,
                        },
                      }}
                    >
                      <SimpleTextCollectionCore
                        params={{
                          ...step.params,
                          attachments: [],
                          templateVariables: config.templateVariables,
                        }}
                        response={currentResponse}
                        setResponse={newResponse => {
                          setCurrentResponse(newResponse);
                          const matchesLastSavedResponse = _.isEqual(
                            newResponse.annotations,
                            lastSavedResponse.annotations,
                          );
                          if (
                            canUpdateCompletedState &&
                            !matchesLastSavedResponse &&
                            !readOnly &&
                            complete
                          ) {
                            markStepAsIncompletedByIndex(responseIndex);
                          } else if (
                            // Explicitly mark the step as completed if the current response is equal to the last saved response
                            canUpdateCompletedState &&
                            matchesLastSavedResponse &&
                            !readOnly &&
                            !complete
                          ) {
                            markStepAsCompletedByIndex(responseIndex);
                          }
                        }}
                        readOnly={readOnly || shouldPreventRetroactiveEdits}
                        chatTaskBehaviorLogMetadata={{
                          responseIndex,
                        }}
                        assignmentId={taskContext.assignmentId}
                        setSubmitCodeError={setSubmitCodeError}
                        codingUnitTestsGenerationConfig={
                          taskContext.enableCodingUnitTestsGeneration
                        }
                        lastPrompt={lastPrompt}
                        lastModelResponseEditorContent={
                          lastModelResponseEditorResponse?.output as string
                        }
                        projectId={taskContext.projectId}
                      />
                    </ChatStepChildIdContext.Provider>
                  </div>
                </div>

                <AutoCritiqueAlert
                  autoCritiqueHook={autoCritiqueHook}
                  headerLabel={autoCritiqueLabel}
                />

                <StepSubmissionConfirmationModal
                  open={showSubmitConfirmationModal}
                  title="Are you sure you want to save this step?"
                  description="Once you confirm that you want to save this step, you will not be able to edit it afterwards."
                  onConfirm={async isConfirmed => {
                    setShowSubmitConfirmationModal(false);
                    if (isConfirmed) {
                      await handleSubmitStep();
                    }
                  }}
                />
              </div>
            </StepCard.Body>

            {!taskContext.previewMode && (
              <StepCard.Footer className="flex justify-end">
                {autoCritiqueConfig?.enabled && (
                  <AutoCritiqueButton
                    className="ml-auto mr-2"
                    onClick={fetchAutoCritique}
                    loading={autoCritiqueHook.critiqueData.loading}
                    buttonLabel={autoCritiqueLabel}
                  />
                )}
                <Tooltip
                  text={submitTooltip}
                  side="top"
                  disabled={canSubmit}
                  target={
                    <div>
                      <Button
                        onClick={async () => {
                          if (step.params.prevent_retroactive_edits) {
                            // NOTE: Workflow here is a bit weird. If retroactive edits are disabled, then instead of submitting the step
                            // immediately, we want to show a confirmation modal (see `StepSubmissionConfirmationModal` above). Submitting
                            // the step will be handed off to that modal instead of this button. Ideally, this workflow could be made more
                            // clear and streamlined in the future.
                            setShowSubmitConfirmationModal(true);
                            return;
                          }
                          await handleSubmitStep();
                        }}
                        fullWidth
                        disabled={!canSubmit}
                        data-testid="submit-textcollection-button"
                      >
                        {t('save-and-continue')}
                      </Button>
                    </div>
                  }
                />
              </StepCard.Footer>
            )}
          </StepCard>
        </StepWidthWrapper>
      </CardAlert>
    );
  };

export default memo(TextCollectionTaskComponent);
